import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { LayerVisibility } from "../../components/risk/geoUtils";
import { useAppSelector } from "../../app/hooks";


// Define an enum for map styles with name and ID
export enum MapStyle
    {
    Light = "mapbox://styles/furlong-metsci/clo8xj85300p801qxcj6uev97",
    Dark = "mapbox://styles/mapbox/dark-v11",
    Terrain = "mapbox://styles/mapbox/outdoors-v12",
    Satellite = "mapbox://styles/mapbox/satellite-streets-v12"
}

export enum OpenRoadsStatus
    {
    Loading = "loading",
    Loaded = "loaded",
    Empty = "empty",
}

export const initialMapStyle = MapStyle.Light;

// Define the types for your states
interface MapDisplaySlice
{
    opacity: number;
    mapStyle: MapStyle;
    layerVisibility: LayerVisibility;
    openRoadsVisibility: boolean;
    openRoadsStatus: OpenRoadsStatus;
    isShowingMarkerPopup: boolean;
    isShowingWindStreamlines: boolean;
    isShowingWindStreamlinesTuningPanel: boolean;
}

const initialMapDisplayState: MapDisplaySlice = {
    opacity: 0.7,
    mapStyle: initialMapStyle,
    layerVisibility: {
        IconLegend: false,
        Great: true,
        Slight: true,
        Moderate: true,
        Challenging: true,
        Hazardous: true,
        Treacherous: true,
        Route: true,
    },
    openRoadsVisibility: false,
    openRoadsStatus: OpenRoadsStatus.Loading,
    isShowingMarkerPopup: false,
    isShowingWindStreamlines: false,
    isShowingWindStreamlinesTuningPanel: false,
};

export const mapDisplaySlice = createSlice( {
    name: "mapDisplay",
    initialState: initialMapDisplayState,
    reducers: {
        setOpacity( state, action: PayloadAction<number> )
        {
            state.opacity = action.payload;
        },
        setMapStyleState( state, action: PayloadAction<MapStyle> )
        {
            state.mapStyle = action.payload;
        },
        setLayerVisbilityState( state, action: PayloadAction<LayerVisibility> )
        {
            state.layerVisibility = action.payload;
        },
        resetLayerVisibilityState( state )
        {
            state.layerVisibility = initialMapDisplayState.layerVisibility;
        },
        toggleOpenRoadsVisibility( state )
        {
            state.openRoadsVisibility = !state.openRoadsVisibility;
        },
        setOpenroadsVisibilityToFalse( state )
        {
            state.openRoadsVisibility = false;
        },
        setOpenRoadStopsPresent( state )
        {
            state.openRoadsStatus = OpenRoadsStatus.Loaded;
        },
        setOpenRoadStopsAbsent( state )
        {
            state.openRoadsStatus = OpenRoadsStatus.Loading;
        },
        setOpenRoadsQueryFinished( state )
        {
            if ( state.openRoadsStatus === OpenRoadsStatus.Loading )
            {
                state.openRoadsStatus = OpenRoadsStatus.Empty;
            }
        },
        setIsShowingMarkerPopup( state, action: PayloadAction<boolean> )
        {
            state.isShowingMarkerPopup = action.payload;
        },
        toggleIsShowingWindStreamlines( state )
        {
            state.isShowingWindStreamlines = !state.isShowingWindStreamlines;
        },
        toggleIsShowingWindStreamlinesTuningPanel( state )
        {
            state.isShowingWindStreamlinesTuningPanel = !state.isShowingWindStreamlinesTuningPanel;
        },
    }
} );

// Custom selectors
export const selectOpacity = ( state: RootState ) => state.mapDisplay.opacity;
export const selectMapStyle = ( state: RootState ) => state.mapDisplay.mapStyle;
export const selectLayerVisibility = ( state: RootState ) => state.mapDisplay.layerVisibility;
export const selectOpenRoadsVisibility = ( state: RootState ) => state.mapDisplay.openRoadsVisibility;
export const selectOpenRoadsStatus = ( state: RootState ) => state.mapDisplay.openRoadsStatus;
export const selectIsShowingMarkerPopup = ( state: RootState ) => state.mapDisplay.isShowingMarkerPopup;
export const selectIsShowingWindStreamlines = ( state: RootState ) => state.mapDisplay.isShowingWindStreamlines;
export const selectIsShowingWindStreamlinesTuningPanel = ( state: RootState ) => state.mapDisplay.isShowingWindStreamlinesTuningPanel;

export const useOpacity = () => useAppSelector( selectOpacity );
export const useMapStyle = () => useAppSelector( selectMapStyle );
export const useLayerVisibilityState = () => useAppSelector( selectLayerVisibility );
export const useOpenRoadsVisibility = () => useAppSelector( selectOpenRoadsVisibility );
export const useOpenRoadsStatus = () => useAppSelector( selectOpenRoadsStatus );
export const useIsShowingMarkerPopup = () => useAppSelector( selectIsShowingMarkerPopup );
export const useIsShowingWindStreamlines = () => useAppSelector( selectIsShowingWindStreamlines );
export const useSelectIsShowingWindStreamlinesTuningPanel = () => useAppSelector( selectIsShowingWindStreamlinesTuningPanel );

export const {
    setOpacity,
    setMapStyleState,
    setLayerVisbilityState,
    resetLayerVisibilityState,
    toggleOpenRoadsVisibility,
    setOpenRoadStopsPresent,
    setOpenRoadStopsAbsent,
    setOpenroadsVisibilityToFalse,
    setOpenRoadsQueryFinished,
    setIsShowingMarkerPopup,
    toggleIsShowingWindStreamlines,
    toggleIsShowingWindStreamlinesTuningPanel,
} = mapDisplaySlice.actions;
